import { createFileRoute } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import { Grid, Icon } from '~/components/ui';
import { pageHeadMeta } from '~/lib/seo';

export const Route = createFileRoute('/health-insurance')({
  head: () =>
    pageHeadMeta({
      title: 'Safetywing x nsave – Your remote health insurance',
      description:
        'Remote Health is a insurance that covers Remote Workers across the world. You can use it at any hospital or doctor worldwide. No deductibles or co-pays, 24/7 live human support and paperless claims!',
      path: '/health-insurance',
    }),
  component: HealthInsurancePage,
});

// TODO(copy): placeholder marketing copy — refine wording before launch.
const healthHeroTitle = 'Remote Health is a insurance that covers Remote Workers across the world';

// TODO(copy): placeholder feature-row copy — refine wording before launch.
const healthHeroFeatures = [
  {
    title: 'Global coverage',
    description: 'Covered in 175+ countries around the world.',
  },
  {
    title: 'Flexible plans',
    description: 'No long-term contracts — cancel anytime.',
  },
  {
    title: 'Comprehensive care',
    description: 'Doctor visits, hospital stays, and emergencies included.',
  },
];

const SAFETYWING_WIDGET_CODE = '92209ece-f24a-4f50-8058-68aae4b74534';

/**
 * The SafetyWing widget embed, isolated inside an <iframe> via `srcDoc`.
 *
 * Why an iframe: the SafetyWing script is a third-party ES module that scans the
 * DOM for `[data-widget-code]` containers and mounts into them EXACTLY ONCE per
 * document. It exposes no re-init API, sets no MutationObserver, and self-guards
 * so it never mounts again after its first execution (even re-injecting a
 * cache-busted copy does nothing). Injecting it into the main document therefore
 * only worked on a hard page load; on client-side (SPA) navigation to this
 * route the freshly rendered container was never mounted into — the "missing /
 * empty widget" bug.
 *
 * Each iframe is its own fresh browsing context with its own document, so the
 * module executes fresh and mounts every time React (re)creates the iframe —
 * i.e. reliably on hard loads AND client-side navigations. The iframe content
 * is rendered by the browser, so the script never runs during SSR.
 * `allow-same-origin` is required because SafetyWing's API rejects the opaque
 * `null` origin otherwise assigned to the sandboxed `srcDoc`. The widget
 * (~1.4–1.6k px tall) scrolls internally within the fixed 960px-tall iframe.
 */
const SAFETYWING_WIDGET_SRCDOC = [
  '<!doctype html>',
  '<html>',
  '<head>',
  '<meta charset="utf-8">',
  '<meta name="viewport" content="width=device-width, initial-scale=1">',
  '<style>html,body{margin:0;padding:0;background:transparent}</style>',
  '</head>',
  '<body>',
  `<div id="sw-rh-widget" data-widget-code="${SAFETYWING_WIDGET_CODE}"></div>`,
  `<script type="module" src="https://safetywing.com/widget/latest/assets/index.js"></script>`,
  '</body>',
  '</html>',
].join('');

function HealthInsurancePage() {
  return (
    <PageShell headerTransparentUntilScroll>
      {/* Hero mirrors send-money-home: full-width grey band, left-aligned copy +
          checklist on the left (cols 1–4), and the SafetyWing widget on the
          right in cols 5–8 (9-col grid) so it centers on the 6/7 gridline like
          the currency calculator. On mobile it stacks and centers.

          pt matches send-money-home's transparent-header math:
          pt-[136px] = 56 + 64 + 16, md:pt-[160px] = 80 + 64 + 16. */}
      <Section className="bg-grey-50 pt-[136px] pb-16 md:pt-[160px] md:pb-24">
        <Grid className="items-start gap-y-12 lg:items-stretch">
          <div className="col-span-9 flex flex-col lg:col-span-4">
            <div className="flex flex-1 flex-col lg:justify-center">
              <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] text-balance md:text-h2">
                {healthHeroTitle}
              </h1>
              <div className="mt-8 border-t border-transparent" />
              {/* Feature rows matching the send-money-home hero left column
                  (check icon + bold title + description), left-aligned. */}
              <ul className="mt-8 flex flex-col gap-8">
                {healthHeroFeatures.map((feature) => (
                  <li key={feature.title} className="flex items-start gap-5">
                    <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-grey-100 text-ink">
                      <Icon name="check" className="size-6" />
                    </span>
                    <div className="flex flex-col gap-5">
                      <h3 className="text-body font-semibold text-ink">{feature.title}</h3>
                      <p className="text-body text-grey-900">{feature.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-span-9 flex justify-center lg:col-start-5 lg:col-span-4">
            {/* SafetyWing widget isolated in an iframe (see SAFETYWING_WIDGET_SRCDOC)
                so it re-mounts reliably on every load AND client-side navigation.
                w-full max-w-[600px] keeps it in the calculator's grid slot at the
                widget's natural desktop width (no width starving); the fixed 960px
                height caps it and the widget scrolls internally within the iframe.
                rounded-[40px] clips it to rounded corners like the calculator card. */}
            <iframe
              title="Remote Health insurance plans by SafetyWing"
              srcDoc={SAFETYWING_WIDGET_SRCDOC}
              sandbox="allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
              className="h-[960px] w-full max-w-[600px] rounded-[40px] border-0"
            />
          </div>
        </Grid>
      </Section>
    </PageShell>
  );
}
